import Vue from 'vue';
import Vuex, { MutationTree, ActionTree, ActionContext } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { RootState } from './types';
import { camera } from './modules/camera';
import { cameraMediaData } from './modules/cameraMediaData';
import style from './modules/style';
import completion from './modules/completion';
import { error } from './modules/error';
import { imageID } from './modules/image-id';
import { terms } from './modules/terms';
import { auth } from './modules/auth';
import { maintenance } from './modules/maintenance';
import { orientation } from './modules/orientation';
import { sensorMotion } from './modules/sensorMotion';
import { i18n } from './modules/i18n';

Vue.use(Vuex);

function createState(): RootState {
  const state: RootState = {} as RootState;
  clearState(state);
  return state;
}

function clearState(state: RootState): void {
  state.accessToken = '';
  state.kycRequestID = 0;
  state.motionDirection = '';
}

export const mutations: MutationTree<RootState> = {
  storeAccessToken(state, payload: string) {
    state.accessToken = payload;
  },

  storeKycRequestId(state, payload: number) {
    state.kycRequestID = payload;
  },

  storeMotionDirection(state, payload: string) {
    state.motionDirection = payload;
  },

  clearState(state: RootState) {
    clearState(state);
  }
};

export const actions: ActionTree<RootState, RootState> = {
  clearState(context: ActionContext<RootState, RootState>) {
    context.dispatch('camera/clearState');
    context.dispatch('cameraMediaData/clearState');
    context.dispatch('style/clearState');
    context.dispatch('completion/clearState');
    context.dispatch('error/clearState');
    context.dispatch('imageID/clearState');
    context.dispatch('terms/clearState');
    context.dispatch('auth/clearState');
    context.dispatch('maintenance/clearState');
    context.dispatch('orientation/clearState');
    context.dispatch('sensorMotion/clearState');
    context.dispatch('i18n/clearState');
    context.commit('clearState');
  }
};

export default new Vuex.Store<RootState>({
  state: createState,
  mutations,
  actions,
  modules: {
    camera,
    cameraMediaData,
    style,
    completion,
    error,
    imageID,
    terms,
    auth,
    maintenance,
    orientation,
    sensorMotion,
    i18n
  },
  plugins: [
    createPersistedState({
      key: 'liquid-ekyc-applicant',
      reducer: (state: any): object => ({
        auth: state.auth,
        terms: {
          agreedTermsOfService: state.auth.kycOrgConfig.agreedTermsOfService
        },
        camera: {
          idDocType: state.camera.idDocType,
          mnDocType: state.camera.mnDocType,
          gpDocType: state.camera.gpDocType,
          opDocType: state.camera.opDocType,
          gpDocPageLast: state.camera.gpDocPageLast,
          opDocTermLast: state.camera.opDocTermLast,
          opDocPageLast: state.camera.opDocPageLast,
          isDetectableIdDocFront: state.camera.isDetectableIdDocFront,
          isDetectableIdDocDiagonal: state.camera.isDetectableIdDocDiagonal,
          isDetectableIdDocBack: state.camera.isDetectableIdDocBack,
          photoID: state.camera.photoID
        },
        sensorMotion: {
          stack: state.sensorMotion.stack
        },
        style: state.style,
        completion: state.completion,
        i18n: { locale: state.i18n.locale }
      })
    })
  ]
});
